import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useHistory } from "react-router-dom";
import StorageKeys from "../../constants/storageKeys";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  FormControl,
} from "@material-ui/core";
import Close from "@material-ui/icons/Close";
import Button from "components/CustomButtons/Button.js";
import SearchAccordion from "./SearchAccordion";
import preferenceCategories from "../../constants/preferenceCategories";
import SearchIcon from "@material-ui/icons/Search";
import ProfilesApi from "../../api_calls/ApiServices";
import { useAuth } from "../../context/AuthContext";

import axios from "axios";
export default function FilterSearchModal({ open, onClose }) {
  const [selectedFilters, setSelectedFilters] = useState({});
  const [dropdowns, setDropdowns] = useState({
    minAge: "",
    maxAge: "",
    country: "",
    region: "",
  });

  const { user } = useAuth();
  const [countries, setCountries] = useState([]);
  const [regions, setRegions] = useState([]);
  const history = useHistory();

  const CATEGORY_KEY_MAP = {
    "Qualities / Personality Traits": "personality_traits",
    "Lifestyle Preferences": "lifestyle_preferences",
    "Relationship Goals": "relationship_goals",
    "Hobbies & Interests": "hobbies",
    "Cultural / Values": "cultural_values",
  };

  const handleDropdownChange = (field) => (event) => {
    const value = event.target.value;
    setDropdowns((prev) => ({
      ...prev,
      [field]: value,
      ...(field === "country" ? { region: "" } : {}),
      ...(field === "minAge" && prev.maxAge && value >= prev.maxAge
        ? { maxAge: "" }
        : {}),
    }));
  };

  useEffect(() => {
    const loadCountries = async () => {
      try {
        const countryData = await ProfilesApi.fetchCountries();
        setCountries(
          countryData.map(({ name, isoCode }) => ({
            isoCode,
            name,
          }))
        );
      } catch (error) {
        console.error("Failed to fetch countries:", error);
      }
    };

    loadCountries();
  }, []);

  useEffect(() => {
    const loadRegions = async () => {
      const selectedCountry = countries.find(
        (c) => c.isoCode === dropdowns.country
      );
      if (!selectedCountry) {
        setRegions([]);
        return;
      }

      try {
        const areaData = await ProfilesApi.fetchCountryAreas(
          selectedCountry.isoCode
        );
        setRegions(areaData);
      } catch (error) {
        console.error("Failed to fetch regions:", error);
        setRegions([]);
      }
    };

    if (dropdowns.country) loadRegions();
  }, [dropdowns.country, countries]);

  const handleApply = async () => {
    const formattedFilters = Object.fromEntries(
      Object.entries(selectedFilters)
        .map(([category, values]) => {
          const mappedKey = CATEGORY_KEY_MAP[category];
          if (!mappedKey) return null;
          const selectedKeys = Object.entries(values)
            .filter(([_, checked]) => checked)
            .map(([key]) => key);
          return [mappedKey, selectedKeys];
        })
        .filter(Boolean)
    );

    const rawFilters = {
      ...dropdowns,
      ...formattedFilters,
    };

    const cleanedFilters = Object.fromEntries(
      Object.entries(rawFilters).filter(([_, value]) => {
        if (Array.isArray(value)) return value.length > 0;
        return value !== "" && value !== null && value !== undefined;
      })
    );

    // ✅ Save to localStorage instead of using URL params
    const payload = {
      user_id: user?.user_id,
      filters: cleanedFilters,
    };

    try {
      localStorage.setItem(StorageKeys.SEARCH_FILTERS, JSON.stringify(payload));
    } catch (e) {
      console.error("Failed to save filters to localStorage", e);
    }

    // 👇 Redirect with clean URL
    history.push("/singles/search-results");

    onClose();
  };

  const renderAgeOptions = (start, end) => {
    const options = [];
    for (let age = start; age <= end; age++) {
      options.push(
        <MenuItem key={age} value={age}>
          {age}
        </MenuItem>
      );
    }
    return options;
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle
        disableTypography
        style={{
          fontSize: "1.25rem",
          fontWeight: 500,
          paddingBottom: 0,
          display: "flex",
          alignItems: "center",
        }}
      >
        <SearchIcon style={{ marginRight: 8, color: "#3f51b5" }} />
        Filter Search
        <Button
          onClick={onClose}
          style={{
            position: "absolute",
            right: 16,
            top: 16,
            padding: "4px 12px",
            backgroundColor: "#f44336",
            color: "#fff",
            textTransform: "none",
            fontWeight: 500,
            fontSize: "0.875rem",
            borderRadius: "4px",
            boxShadow: "none",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = "#d32f2f";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = "#f44336";
          }}
        >
          Close
        </Button>
      </DialogTitle>

      <DialogContent style={{ paddingTop: 20, paddingBottom: 20 }}>
        <Grid container spacing={2} style={{ marginBottom: 20 }}>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth variant="outlined" size="small">
              <InputLabel>Country</InputLabel>
              <Select
                value={dropdowns.country}
                onChange={handleDropdownChange("country")}
                label="Country"
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {countries.map(({ isoCode, name }) => (
                  <MenuItem key={isoCode} value={isoCode}>
                    {name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControl
              fullWidth
              variant="outlined"
              size="small"
              disabled={!regions.length}
            >
              <InputLabel>Region</InputLabel>
              <Select
                value={dropdowns.region}
                onChange={handleDropdownChange("region")}
                label="Region"
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {regions.map(({ id, name }) => (
                  <MenuItem key={id} value={id}>
                    {name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth variant="outlined" size="small">
              <InputLabel>Min Age</InputLabel>
              <Select
                value={dropdowns.minAge}
                onChange={handleDropdownChange("minAge")}
                label="Min Age"
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {renderAgeOptions(18, 99)}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControl fullWidth variant="outlined" size="small">
              <InputLabel>Max Age</InputLabel>
              <Select
                value={dropdowns.maxAge}
                onChange={handleDropdownChange("maxAge")}
                label="Max Age"
                disabled={!dropdowns.minAge}
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {dropdowns.minAge &&
                  renderAgeOptions(parseInt(dropdowns.minAge) + 1, 100)}
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        <SearchAccordion
          categories={preferenceCategories}
          selectedFilters={selectedFilters}
          setSelectedFilters={setSelectedFilters}
        />
      </DialogContent>

      <DialogActions
        style={{ borderTop: "none", paddingTop: 8, paddingBottom: 16 }}
      >
        <Button
          color="danger"
          variant="outlined"
          onClick={handleApply}
          startIcon={<SearchIcon />}
        >
          Search
        </Button>
      </DialogActions>
    </Dialog>
  );
}

FilterSearchModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};
